import React, { useCallback, useEffect, useState, useRef } from "react";
import { SearchResultSummaryBoxTypes } from "./types";
import { FlatList } from "react-native-gesture-handler";
import { Animated, Easing, Platform } from "react-native";
import styled from "styled-components/native";
import { VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { getProductLevelColor, SummaryBoxEnum } from "./Utils";
import { useUserCurrency } from "../../../hooks/getCurrency";

import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components/native";
import { CollectionMenuItem } from "@src/types/models";
import { useAppSettings, useOverlayConfigWeb } from "@redux/slices/globalSlice";
import { useJoyride } from "@components/atoms/Joyride";
import { useSelector } from "react-redux";
import { useFocusEffect } from "@react-navigation/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import {
  formatCurrency,
  formatNumberWithPrecision,
} from "@utils/product/productFormatters";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { isMeleeItem } from "@utils/product/productValidators";

const SearchResultSummaryBox = ({
  summaryOptions,
  selectedItems,
  productType,
  tabletScreenWidth,
  fromBulkList = false,
  isFilterVisible,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
}: SearchResultSummaryBoxTypes) => {
  const getAppSettings = useAppSettings();
  const enableFourDecimalCarat =
    getAppSettings?.enable_four_decimal_carat ?? false;
  const userCurrency = useUserCurrency();
  const { t } = useTranslation();
  const theme = useTheme();
  const [mSummaryOptions, setMSummaryOptions] =
    useState<CollectionMenuItem[]>(summaryOptions);
  const { startTourForScope } = useJoyride() || {};
  const getOverlayConfigWeb = useOverlayConfigWeb();
  const overlayActiveScreensWeb = useSelector(
    (state) => state.appState.overlayActiveScreensWeb
  );
  const flatListRef = useRef<FlatList>(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const scrollX = useRef(new Animated.Value(0)).current;
  const { deviceWidth } = useIsFoldable();
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === "web" && theme.isLargeScreen) {
        if (
          overlayActiveScreensWeb?.BulkActions &&
          selectedItems.length > 0 &&
          getOverlayConfigWeb?.screens?.BulkActions &&
          (getOverlayConfigWeb?.screens?.BulkActions?.overlayActive ===
            undefined ||
            getOverlayConfigWeb?.screens?.BulkActions?.overlayActive ===
              true) &&
          !fromBulkList
        ) {
          startTourForScope("BulkActions");
        }
      }
    }, [overlayActiveScreensWeb?.BulkActions])
  );
  useEffect(() => {
    if (getAppSettings.hide_price_per_carat_and_discount) {
      const mOptions = mSummaryOptions.filter(
        (option) =>
          option.name !== SummaryBoxEnum.DISCOUNT &&
          option.name !== SummaryBoxEnum.PRICE_PER_CARAT
      );
      setMSummaryOptions(mOptions);
    }
  }, [getAppSettings]);

  const isTablet = theme.isLargeScreen || theme.isTablet;
  const screenWidth = isTablet ? tabletScreenWidth : deviceWidth;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let listenerId: string | null = null;

    if (
      selectedItems?.length > 0 &&
      !hasAnimated &&
      flatListRef.current &&
      theme.isMobile
    ) {
      setHasAnimated(true);
      const itemWidth = screenWidth
        ? screenWidth / (mSummaryOptions.length * 2)
        : 100;
      const totalWidth = mSummaryOptions.length * itemWidth;
      timeoutId = setTimeout(() => {
        timeoutId = null;
        Animated.sequence([
          Animated.timing(scrollX, {
            toValue: totalWidth,
            duration: 800,
            useNativeDriver: Platform.OS !== "web",
            easing: Easing.inOut(Easing.cubic),
          }),

          Animated.timing(scrollX, {
            toValue: 0,
            duration: 500,
            useNativeDriver: Platform.OS !== "web",
            easing: Easing.inOut(Easing.cubic),
          }),
        ]).start();
      }, 500);

      listenerId = scrollX.addListener(({ value }) => {
        if (flatListRef.current) {
          flatListRef.current.scrollToOffset({
            offset: value,
            animated: false,
          });
        }
      });
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (listenerId) scrollX.removeListener(listenerId);
    };
  }, [selectedItems.length, hasAnimated, userCurrency]);

  const typography = useTypographyStore();
  let totalCarat = 0;
  let discountPercent = 0;
  let totalPrice = 0;

  const getValue = (type: string) => {
    switch (type) {
      case "num_of_pieces":
        return selectedItems.length;
      case "total_ct":
        selectedItems.forEach((item) => {
          totalCarat += Number.parseFloat(item.size ? item.size : "0");
        });

        const precisionFactor =
          sectionName === SearchFilterTypeEnum.PARCEL
            ? enableFourDecimalCarat
              ? 4
              : 3
            : 2;
        return formatNumberWithPrecision(totalCarat, {
          precision: precisionFactor,
        });
      case "disc":
        if (selectedItems.some((xItem) => xItem?.is_fancy)) {
          return "-";
        } else {
          selectedItems.forEach((item) => {
            discountPercent += Number.parseFloat(
              item.discount_percent ? item.discount_percent : "0"
            );
          });
          return formatNumberWithPrecision(
            discountPercent / selectedItems.length,
            { precision: 2 }
          )?.concat("%");
        }
      case "average_total_price":
        let validItemCount = 0;
        let accPrice = 0;
        let validItemCountMelee = 0;
        selectedItems.forEach((item) => {
          const itemPrice = Number.parseFloat(item.total_sales_price ?? "0");
          if (itemPrice > 0) {
            accPrice += itemPrice;
            validItemCount++;
            if (isMeleeItem(item)) {
              validItemCountMelee++;
            }
          }
        });
        if (validItemCount === 0) return "-";
        return formatCurrency(
          formatNumberWithPrecision(accPrice / validItemCount, {
            precision: validItemCountMelee === validItemCount ? 2 : 0,
          }) ?? "0",
          {
            symbol: userCurrency,
            precision: validItemCountMelee === validItemCount ? 2 : 0,
          }
        );

      case "total_price":
        let validTotalPriceItemCountMelee = 0;
        selectedItems.forEach((item) => {
          if (isMeleeItem(item)) {
            validTotalPriceItemCountMelee++;
          }
          totalPrice += Number.parseFloat(item.total_sales_price ?? "0");
        });
        return formatCurrency(
          formatNumberWithPrecision(totalPrice, {
            precision:
              validTotalPriceItemCountMelee === selectedItems.length ? 2 : 0,
          }) ?? "0",
          {
            symbol: userCurrency,
            precision:
              validTotalPriceItemCountMelee === selectedItems.length ? 2 : 0,
          }
        );
      default:
        let mTotalPrice = 0;
        let mTotalCarat = 0;
        selectedItems.forEach((item) => {
          mTotalPrice += Number.parseFloat(item.total_sales_price ?? "0");
        });
        selectedItems.forEach((item) => {
          mTotalCarat += Number.parseFloat(item.size ? item.size : "0");
        });
        return formatCurrency(
          formatNumberWithPrecision(
            mTotalPrice / (mTotalCarat > 0 ? mTotalCarat : 1),
            { precision: 0 }
          ) ?? "0",
          {
            symbol: userCurrency,
            precision: 0,
          }
        );
    }
  };

  const renderSeparator = () => {
    return <Divider />;
  };

  const CellRenderer = ({
    children,
    index,
    style,
  }: {
    children: React.ReactNode;
    index: number;
    style: any;
  }) => (
    <CellWrapper style={style} index={index} itemCount={mSummaryOptions.length}>
      {children}
    </CellWrapper>
  );

  return (
    <FlatList
      {...(theme.isMobile && { ref: flatListRef })}
      data={mSummaryOptions}
      horizontal={true}
      ItemSeparatorComponent={renderSeparator}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        flexGrow: 1,
        backgroundColor: theme[getProductLevelColor(productType)],
        ...(theme.isMobile
          ? {}
          : {
              alignItems: "center",
              justifyContent: "center",
            }),
        ...(theme.isLargeScreen || (theme?.isTablet && fromBulkList)
          ? { paddingEnd: 6 }
          : {}),
        height: theme?.isLargeScreen
          ? "auto"
          : theme?.isTablet && fromBulkList
          ? 34
          : 56,
        ...(theme.isLargeScreen || (theme?.isTablet && fromBulkList)
          ? { borderRadius: 8 }
          : {}),
      }}
      CellRendererComponent={CellRenderer}
      renderItem={({ item, index }) => (
        <SummaryItem
          screenWidth={screenWidth}
          bgColor={theme[getProductLevelColor(productType)]}
        >
          <SummaryContent
            data={mSummaryOptions}
            index={index}
            theme={theme}
            isFilterVisible={isFilterVisible}
            cellWidth={
              theme.isMobile
                ? mSummaryOptions?.length > 3
                  ? screenWidth / 4.25
                  : screenWidth / mSummaryOptions?.length
                : screenWidth / (mSummaryOptions?.length * 2)
            }
            bgColor={theme[getProductLevelColor(productType)]}
          >
            <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
              {getValue(item.name)}
            </VDBText>
            <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
              {t(item.label)}
            </VDBText>
          </SummaryContent>
        </SummaryItem>
      )}
      keyExtractor={(item) => item.name}
    />
  );
};

const SummaryItem = styled.View<any>`
  flex-direction: row;
  padding-top: ${({ theme }) => (theme.isLargeScreen ? 8 : 8)}px;
  padding-bottom: ${({ theme }) => (theme.isLargeScreen ? 8 : 8)}px;
  background-color: ${({ bgColor }) => bgColor};
  ${({ theme }) =>
    theme.isDesktop || theme.isTablet ? "border-radius: 8px;" : ""};
`;

const SummaryContent = styled.View<any>`
  ${({ cellWidth, theme }) =>
    theme.isLargeScreen ? "flex-grow: 1;" : `width: ${cellWidth}px;`}
  flex-direction: ${({ theme, isFilterVisible }) =>
    isFilterVisible && theme.isTablet
      ? "column"
      : theme.isLargeScreen || theme.isTablet
      ? "row"
      : "column"};
  ${({ theme }) => (theme.isLargeScreen ? "min-width: 130px; gap: 4px;" : "")}
  align-items: center;
  justify-content: center;
  margin-start: 4px;
  background-color: ${({ bgColor }) => bgColor};
  ${({ theme }) => theme.isTablet && "gap: 4px;"};
`;

const CellWrapper = styled.View<{
  itemCount: number;
  index: number;
}>`
  flex: 1;
  align-items: center;
  padding: ${({ theme }) => (theme.isLargeScreen ? "4px" : "0px")};
  justify-content: center;
`;

const Divider = styled.View`
  position: absolute;
  right: 0;
  height: ${({ theme }) =>
    theme.isMobile ? SpaceType.ExtraLarge : SpaceType.Double}px;
  width: 1px;
  background-color: ${({ theme }) => theme["diam-05"]};
`;

export default SearchResultSummaryBox;
